import { useEffect, useRef, useState } from 'react';
import {
  FirebaseService,
  httpService,
  IconWrapper,
  auth,

} from '@flexo/general';
import { Textfield, Button, PasswordConditions, PasswordValidation, validatePassword, isPasswordValid } from '@flexo/atoms';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import styles from './update-credentials-form.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RIGHT_DRAWER, USER, ALERT, MODAL } from '@flexo/reducers';
import { Player } from '@lottiefiles/react-lottie-player';

const firebaseService = new FirebaseService();

const ENV = (import.meta as any).env;

const debounce = (fn, delay = 100) => {
  let timer: any = null;
  return function (...args: any[]) {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      timer = null;
      fn(...args);
    }, delay);
  };
};

export function UpdateCredentialsForm() {
  const variant = ENV?.VITE_APP_IS_WEB === 'true' ? 'web' : 'account';
  const user = useSelector((state: any) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formRef = useRef<any>(null);

  const { t } = useTranslation();
  const { title, subtitle, inputs, buttonLabel } = t(
    'drawers.update_credentials',
    { returnObjects: true }
  ) as any;

  const [formData, setFormData] = useState({
    displayName:
      user?.auth?.currentUser?.displayName ||
      auth?.currentUser?.displayName ||
      '',
    email:
      auth?.currentUser?.email ||
      user?.tokenContent?.firebaseUserResult?.email ||
      '',
    password: '',
    password_confirm: '',
  });

  // Per-field blur state (similar to account.tsx)
  const [fieldBlurred, setFieldBlurred] = useState({
    displayName: false,
    email: false,
    password: false,
    password_confirm: false,
  });

  const [formError, setFormError] = useState(false);
  const [formRequestSent, setFormRequestSent] = useState(false);

  const [samePassword, setSamePassword] = useState(true);

  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    lengthValid: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasUpperCase: false,
    hasLowerCase: false,
    passwordsMatch: false,
  });

  const [passwordBlur, setPasswordBlur] = useState(false);

  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    password_confirm: false,
  });

  const [showSuccessfulAnimation, setShowSuccessfulAnimation] =
    useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(true);

  // Use Account styles if variant === 'account'
  const useAccountStyles = variant === 'account';

  async function handleInputChange(event: any) {
    const { name, value } = event.target;

    if (name === 'password_confirm' && samePassword === false) {
      setSamePassword(true);
    }

    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);

    if (name === 'password') {
      checkValidation(value);
      // If password is emptied, clear any confirm password error
      if (value === '' && samePassword === false) {
        setSamePassword(true);
      }
    }
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;
    setFieldBlurred((prev) => ({
      ...prev,
      [name]: true,
    }));
    
    if (name === 'password') {
      setPasswordBlur(true);
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;
    setFieldBlurred((prev) => ({
      ...prev,
      [name]: false,
    }));

    // Clear password mismatch error when refocusing on confirm password field
    if (name === 'password_confirm' && samePassword === false) {
      setSamePassword(true);
    }
    
    if (name === 'password') {
      setPasswordBlur(false);
    }
  };

  function togglePasswordVisibility(
    inputName: keyof typeof passwordVisibility
  ) {
    setPasswordVisibility((prev) => ({
      ...prev,
      [inputName]: !prev[inputName],
    }));
  }

  // Helper functions to get derived values
  const getPasswordType = (fieldName: keyof typeof passwordVisibility) =>
    passwordVisibility[fieldName] ? 'text' : 'password';

  const getPasswordIcon = (fieldName: keyof typeof passwordVisibility) =>
    passwordVisibility[fieldName] ? 'show' : 'hide';

  function checkValidation(value: string) {
    // Use standardized password validation
    const validation = validatePassword(value, formData.password_confirm);
    setPasswordValidation(validation);
  }

  function checkIfSubmitIsDisabled() {
    // Check if form has any meaningful changes
    const initialDisplayName =
      user?.auth?.currentUser?.displayName ||
      auth?.currentUser?.displayName ||
      '';
    const initialEmail =
      auth?.currentUser?.email ||
      user?.tokenContent?.firebaseUserResult?.email ||
      '';

    const hasChanges =
      formData.displayName !== initialDisplayName ||
      formData.password !== '' ||
      formData.password_confirm !== '';

    if (!hasChanges) {
      return true;
    }

    // Require displayName to not be empty
    if (formData.displayName === '') {
      return true;
    }

    // If password fields have content, they must match
    if (formData.password !== '' || formData.password_confirm !== '') {
      if (formData.password !== formData.password_confirm) {
        return true;
      }

      // Use standardized password validation
      if (
        formData.password !== '' &&
        !isPasswordValid(formData.password)
      ) {
        return true;
      }
    }

    return false;
  }

  async function submitForm() {
    try {
      // Set form request state
      setFormRequestSent(true);

      // Get Firebase token
      const tokenID = await firebaseService.getIdToken();

      // Handle session expiration
      if (!tokenID) {
        dispatch(
          ALERT.SetAlert({
            value: { type: 'BANNER_SESSION_EXPIRED' },
          } as any)
        );

        dispatch(USER.SetLogout());
        return;
      }

      // Send update request to server
      const response = await httpService
        .put({
          idToken: tokenID,
          url: `${ENV.VITE_APP_BASE_URL}/auth/v1_1/update-profile`,
          data: {
            ...(formData?.displayName !== ''
              ? { display_name: formData.displayName }
              : {}),
            ...(formData?.email !== '' ? { email: formData.email } : {}),
            ...(formData?.password !== ''
              ? { new_password: formData.password }
              : {}),
          },
        })
        .then((resp) => ({
          status: resp.status,
          statusText: resp?.statusText || 'Unknown status text',
          message: resp?.data?.message || 'Unknown message',
        }))
        .catch((error) => ({
          status: error?.response?.status || 500,
          statusText: error?.response?.statusText || 'Unknown error',
        }));

      // Handle error response
      if (response.status > 200) {
        setFormError(true);
        return response.status;
      }

      // Clear any existing alerts and wait a moment
      setFormError(false);
      dispatch(ALERT.ResetAlert());
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Check if this is only a username update (no password change)
      const userEmail = await firebaseService.getCurrentUserEmail();
      const isOnlyUsernameUpdate =
        formData?.email === userEmail && formData?.password === '';

      // Update the display name in memory
      const newUserData = { ...user };
      if (newUserData?.auth?.currentUser?.displayName) {
        newUserData.auth.currentUser.displayName = formData.displayName;
      } else {
        try {
          newUserData['auth'] = auth;
          newUserData.auth.currentUser.displayName = formData.displayName;
        } catch (err) {
          newUserData['auth'] = {};
          newUserData.auth['currentUser'] = {};
          newUserData.auth.currentUser['displayName'] = formData.displayName;
        }
      }

      if (auth?.currentUser?.displayName) {
        (auth.currentUser as any).displayName = formData.displayName;
      }

      // Handle the two update scenarios
      if (isOnlyUsernameUpdate) {
        // SCENARIO 1: Username-only update - stay on current page

        // First update Redux state with new display name
        dispatch(USER.SetUserAsync(newUserData) as any);
        // Close the drawer last with a small delay to ensure UI updates correctly
        dispatch(MODAL.CloseModal());
        dispatch(RIGHT_DRAWER.HideDrawer());
        dispatch(RIGHT_DRAWER.ResetDrawer());

        // Show success message for username change first before closing the drawer
        dispatch(
          ALERT.SetAlert({
            value: { type: 'BANNER_CHANGE_USERNAME_SUCCESS' },
          } as any)
        );
      } else {
        // Close the drawer or modal first
        dispatch(MODAL.CloseModal());
        dispatch(ALERT.ResetAlert());

        dispatch(USER.ShowSplashScreen());
        dispatch(USER.SetIsLoggingOut());

        // Clear password fields since update was successful
        setFormData((prevData) => ({
          ...prevData,
          password: '',
          password_confirm: '',
        }));

        dispatch(RIGHT_DRAWER.HideDrawer());
        dispatch(RIGHT_DRAWER.ResetDrawer());

        // Set flags for the login page to show success message
        localStorage.setItem('passwordUpdated', 'true');
        sessionStorage.setItem('passwordUpdated', 'true');

        // Perform the Firebase signout in the background
        setTimeout(() => {
          firebaseService
            .signOut()
            .catch((error) => console.error('Firebase signout failed:', error));
        }, 100);

        dispatch(USER.SetLogout());
        navigate('/login');
      }
    } catch (error) {
      console.error('Error updating credentials:', error);
      setFormError(true);
    } finally {
      // Reset request state
      setFormRequestSent(false);
    }
  }

  function askForSubmissionConfirmation() {
    dispatch(
      ALERT.SetAlert({
        value: {
          type: 'ALERT_CONFIRM_CREDENTIALS_CHANGES',
          callback: async () => {
            return await submitForm();
          },
        } as any,
      })
    );
  }

  function checkConfirmationPassword(e: any) {
    if (e.type === 'keyup' && e.key === 'Backspace') {
      setSamePassword(true);
    } else if (e.type === 'blur') {
      const checkMatch = () => {
        if (formData.password !== formData.password_confirm) {
          setSamePassword(false);
        } else {
          setSamePassword(true);
        }
      };

      debounce(checkMatch, 1)();
    }
  }

  function close() {
    dispatch(RIGHT_DRAWER.ResetDrawer());
  }

  function focusChild(event: any) {
    try {
      const parent = event.target;
      const child = parent.querySelector('input');
      child.focus();
    } catch (err: any) {
      console.warn(err);
    }
  }

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        close();
      }
      if (
        event.key === 'Enter' &&
        formRef?.current?.contains(document.activeElement)
      ) {
        event.preventDefault();
        if (!checkIfSubmitIsDisabled()) {
          askForSubmissionConfirmation();
        }
      }
    }

    // Aggiungi l'event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup per rimuovere l'event listener quando il componente si smonta
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [formRequestSent, checkIfSubmitIsDisabled]); // Dependencies for proper effect handling

  useEffect(() => {
    if (ENV?.VITE_APP_IS_WEB !== 'true') {
      const handleFocus = (event: FocusEvent) => {
        (event.target as HTMLElement).scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      };
      const inputs = document.querySelectorAll('input');
      inputs.forEach((input) => {
        input.addEventListener('focus', handleFocus);
      });
      // Cleanup
      return () => {
        inputs.forEach((input) => {
          input.removeEventListener('focus', handleFocus);
        });
      };
    }
  }, []);

  return (
    <div
      className={
        useAccountStyles ? styles.Account : styles.UpdateCredentialsForm
      }
    >
      {showForm ? (
        <>
          {variant !== 'account' && (
            <div
              className={
                useAccountStyles
                  ? undefined
                  : styles.UpdateCredentialsForm__Header
              }
            >
              <div className={styles.UpdateCredentialsForm__Header__Title}>
                {title}
                <div
                  className={styles.UpdateCredentialsForm__Header__Title__Close}
                  onClick={() => close()}
                >
                  <IconWrapper iconName={'cross'} />
                </div>
              </div>
              <div className={styles.UpdateCredentialsForm__Header__Subtitle}>
                {subtitle}
              </div>
            </div>
          )}
          <form
            id={`account_creation_${variant}_form`}
            className={
              useAccountStyles
                ? styles.Account__Information__Textfield__Wrapper
                : styles.UpdateCredentialsForm__Form
            }
            ref={formRef}
            style={useAccountStyles ? { marginTop: 30, paddingLeft: 4 } : {}}
          >
            {/* Nome */}

            <div
              className={
                useAccountStyles
                  ? styles.Account__Information__Textfield
                  : styles.UpdateCredentialsForm__Form__Input
              }
            >
              <Textfield
                label={inputs.generalities.label}
                name={inputs.generalities.name}
                type={inputs.generalities.type}
                placeholder={inputs.generalities.placeholder}
                variant="default"
                onPaste={(e) => false}
                onCopy={(e) => false}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                autoComplete="off"
                defaultValue={formData[inputs.generalities.name]}
                onChange={(ev: any) => handleInputChange(ev)}
                onBlur={handleBlur}
                onFocus={handleFocus}
                containedLabel={useAccountStyles ? true : undefined}
                color="white"
              />
            </div>
            {/* Email */}
            <div
              className={
                useAccountStyles
                  ? styles.Account__Information__Textfield
                  : `${styles.UpdateCredentialsForm__Form__Input} ${styles.UpdateCredentialsForm__Form__Input__Disabled}`
              }
            >
              <Textfield
                variant="default"
                label={inputs.email.label}
                name={inputs.email.name}
                type={inputs.email.type}
                placeholder={inputs.email.placeholder}
                color={'white'}
                disabled={true}
                onPaste={(e) => false}
                onCopy={(e) => false}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                autoComplete="off"
                defaultValue={formData[inputs.email.name]}
                additionalLabelStyles={
                  styles.UpdateCredentialsForm__Form__Input__UpperLabel
                }
                containedLabel={false}
              />
            </div>
            {/* Password */}
            <div
              className={
                useAccountStyles
                  ? styles.Account__Information__Textfield
                  : styles.UpdateCredentialsForm__Form__Input
              }
              onClick={(e) => focusChild(e)}
            >
              <Textfield
                variant="default"
                label={inputs.password.label}
                color="white"
                name={inputs.password.name}
                type={getPasswordType(inputs.password.name)}
                placeholder={inputs.password.placeholder}
                onPaste={(e) => false}
                onCopy={(e) => false}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                autoComplete="off"
                defaultValue={formData[inputs.password.name]}
                onChange={(ev: any) => handleInputChange(ev)}
                onBlur={handleBlur}
                onFocus={handleFocus}
                icon={getPasswordIcon(inputs.password.name)}
                onIconClick={() =>
                  togglePasswordVisibility(inputs.password.name)
                }
                containedLabel={useAccountStyles ? true : undefined}
                value={formData[inputs.password.name]}
              />
            </div>
            {/* Password Conditions */}
            <div
              className={
                useAccountStyles
                  ? styles.Account__Information__Textfield
                  : styles.UpdateCredentialsForm__Form__Input
              }
              style={useAccountStyles ? { marginBottom: 0 } : {}}
            >
              <div
                className={styles.UpdateCredentialsForm__Form__Checklist__Title}
              >
                {inputs.checkList_title}
              </div>
              <PasswordConditions
                validation={passwordValidation}
                isFocused={!passwordBlur}
                className={styles.UpdateCredentialsForm__Form__PasswordConditions}
              />
            </div>
            {/* confirm Password */}
            <div
              className={
                useAccountStyles
                  ? styles.Account__Information__Textfield
                  : styles.UpdateCredentialsForm__Form__Input
              }
              onClick={(e) => focusChild(e)}
            >
              <Textfield
                variant="default"
                label={inputs.password_confirm.label}
                color="white"
                name={inputs.password_confirm.name}
                type={getPasswordType(inputs.password_confirm.name)}
                placeholder={inputs.password_confirm.placeholder}
                onPaste={(e) => false}
                onCopy={(e) => false}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                autoComplete="off"
                defaultValue={formData[inputs.password_confirm.name]}
                onChange={handleInputChange}
                onBlur={(e) => {
                  handleBlur(e);
                  checkConfirmationPassword(e);
                }}
                onFocus={handleFocus}
                icon={getPasswordIcon(inputs.password_confirm.name)}
                onIconClick={() =>
                  togglePasswordVisibility(inputs.password_confirm.name)
                }
                onKeyUp={(e) => checkConfirmationPassword(e)}
                underline={samePassword === false ? 'visible' : 'hidden'}
                webError={!samePassword}
                error={
                  !samePassword ? inputs.password_confirm.descriptionFault : ''
                }
                containedLabel={useAccountStyles ? true : undefined}
                value={formData[inputs.password_confirm.name]}
              />
            </div>
          </form>
          <div
            className={
              useAccountStyles ? '' : styles.UpdateCredentialsForm__Footer
            }
          >
            <Button
              className={
                ENV?.VITE_APP_IS_WEB !== 'true'
                  ? styles.UpdateCredentialsForm__Footer__Button__Mobile
                  : styles.UpdateCredentialsForm__Footer__Button
              }
              label={buttonLabel}
              onClick={() => askForSubmissionConfirmation()}
              disabled={checkIfSubmitIsDisabled()}
              style={useAccountStyles ? { width: '100%', minHeight: 48 } : {}}
            />
          </div>
        </>
      ) : (
        showSuccessfulAnimation && (
          <div className={styles.UpdateCredentialsForm__Success__Container}>
            <div className={styles.UpdateCredentialsForm__Success}>
              <Player
                autoplay
                loop={false}
                src={'/trophyAnimation.json'}
                style={{ height: 150, marginBottom: 50 }}
              />
              <span
                className={`${styles.UpdateCredentialsForm__Success__Title} heading3`}
              >
                {t('modals.updateCredentialsForm.successTitle')}
              </span>
              <span
                className={`${styles.UpdateCredentialsForm__Success__Subtitle} paragraph`}
              >
                {t('modals.updateCredentialsForm.successSubtitle')}
              </span>
            </div>
          </div>
        )
      )}
    </div>
  );
}
